import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Mail, Phone, Check } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { ClientesService, type RegistroClienteDto } from '../../services/clientes/clientes.service';
import toast from 'react-hot-toast';

// Esquema de validación
const registroClienteSchema = z.object({
  nombres: z.string().min(2, 'Los nombres son obligatorios').max(100, 'Máximo 100 caracteres'),
  apellidos: z.string().min(2, 'Los apellidos son obligatorios').max(100, 'Máximo 100 caracteres'),
  telefono: z.string().min(7, 'El teléfono debe tener al menos 7 dígitos').max(20, 'Máximo 20 caracteres'),
  email: z.string().email('Email inválido').max(255, 'Máximo 255 caracteres'),
  consentimientoDatos: z.boolean().refine(val => val === true, {
    message: 'Debe aceptar el tratamiento de datos personales'
  })
});

type FormData = z.infer<typeof registroClienteSchema>;

interface Props {
  onSuccess?: (email: string) => void;
  onCancel?: () => void;
  className?: string;
}

export const RegistroClienteForm = ({ onSuccess, onCancel, className }: Props) => {
  const { theme } = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch
  } = useForm<FormData>({
    resolver: zodResolver(registroClienteSchema),
    mode: 'onChange',
    defaultValues: {
      nombres: '',
      apellidos: '',
      telefono: '',
      email: '',
      consentimientoDatos: false
    }
  });

  const consentimientoDatos = watch('consentimientoDatos');

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      const response = await ClientesService.registrarCliente(data);
      
      if (response.success) {
        toast.success(response.message || '¡Registro exitoso! Revisa tu correo para verificar tu cuenta.');
        reset();
        onSuccess?.(data.email);
      } else {
        toast.error(response.message || 'Error al registrar cliente');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error instanceof Error ? error.message : 'Error al registrar cliente');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`${className} max-w-md mx-auto`}>
      <div className={`rounded-2xl p-8 ${
        theme === 'dark' 
          ? 'bg-gray-800' 
          : 'bg-white shadow-xl'
      }`}>
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Registro de Cliente
          </h2>
          <p className={`mt-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Crea tu cuenta para acceder a nuestros servicios
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Nombres */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Nombres *
            </label>
            <input
              type="text"
              {...register('nombres')}
              className={`w-full px-4 py-3 rounded-lg border-2 transition-colors ${
                errors.nombres
                  ? 'border-red-500 focus:border-red-500'
                  : theme === 'dark'
                    ? 'border-gray-600 bg-gray-700 text-white focus:border-emerald-500'
                    : 'border-gray-200 bg-white text-gray-900 focus:border-emerald-500'
              }`}
              placeholder="Ingresa tus nombres"
            />
            {errors.nombres && (
              <p className="text-red-500 text-sm mt-1">{errors.nombres.message}</p>
            )}
          </div>

          {/* Apellidos */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Apellidos *
            </label>
            <input
              type="text"
              {...register('apellidos')}
              className={`w-full px-4 py-3 rounded-lg border-2 transition-colors ${
                errors.apellidos
                  ? 'border-red-500 focus:border-red-500'
                  : theme === 'dark'
                    ? 'border-gray-600 bg-gray-700 text-white focus:border-emerald-500'
                    : 'border-gray-200 bg-white text-gray-900 focus:border-emerald-500'
              }`}
              placeholder="Ingresa tus apellidos"
            />
            {errors.apellidos && (
              <p className="text-red-500 text-sm mt-1">{errors.apellidos.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Email *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                {...register('email')}
                className={`w-full pl-12 pr-4 py-3 rounded-lg border-2 transition-colors ${
                  errors.email
                    ? 'border-red-500 focus:border-red-500'
                    : theme === 'dark'
                      ? 'border-gray-600 bg-gray-700 text-white focus:border-emerald-500'
                      : 'border-gray-200 bg-white text-gray-900 focus:border-emerald-500'
                }`}
                placeholder="tu@email.com"
              />
            </div>
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* Teléfono */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Teléfono *
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                {...register('telefono')}
                className={`w-full pl-12 pr-4 py-3 rounded-lg border-2 transition-colors ${
                  errors.telefono
                    ? 'border-red-500 focus:border-red-500'
                    : theme === 'dark'
                      ? 'border-gray-600 bg-gray-700 text-white focus:border-emerald-500'
                      : 'border-gray-200 bg-white text-gray-900 focus:border-emerald-500'
                }`}
                placeholder="300 123 4567"
              />
            </div>
            {errors.telefono && (
              <p className="text-red-500 text-sm mt-1">{errors.telefono.message}</p>
            )}
          </div>

          {/* Consentimiento */}
          <div>
            <label className="flex items-start space-x-3 cursor-pointer">
              <div className="relative flex-shrink-0 mt-1">
                <input
                  type="checkbox"
                  {...register('consentimientoDatos')}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                  consentimientoDatos
                    ? 'bg-emerald-500 border-emerald-500'
                    : errors.consentimientoDatos
                      ? 'border-red-500'
                      : theme === 'dark'
                        ? 'border-gray-600'
                        : 'border-gray-300'
                }`}>
                  {consentimientoDatos && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
              </div>
              <span className={`text-sm ${
                errors.consentimientoDatos ? 'text-red-500' : theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Acepto el tratamiento de mis datos personales de acuerdo con la política de privacidad de FarmaSalud. *
              </span>
            </label>
            {errors.consentimientoDatos && (
              <p className="text-red-500 text-sm mt-1">{errors.consentimientoDatos.message}</p>
            )}
          </div>

          {/* Botones */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className={`flex-1 py-3 px-6 rounded-lg font-medium transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="flex-1 py-3 px-6 rounded-lg font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-emerald-600 hover:bg-emerald-700"
            >
              {isSubmitting ? 'Registrando...' : 'Registrar Cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};